import { useCallback, useMemo } from 'react';
import { useRxData } from 'rxdb-hooks';

import { TRUE } from '../../utilities/constants';
import { ComponentRemovalCategories, InspectionStatus } from '../../utilities/enums';
import { DeficiencyActionedList } from '../deficiencyActionedList/queryBuilder';
import useDeficiencyActionedList from '../deficiencyActionedList/useDeficiencyActionedList';
import { DeficiencyComponentRemoval } from '../deficiencyComponentRemoval/queryBuilder';
import useDeficiencyComponentRemoval from '../deficiencyComponentRemoval/useDeficiencyComponentRemoval';
import { DeficiencyFiles, DeficiencyFilesCollection } from '../deficiencyFiles/queryBuilder';
import {
  DeficiencyVersionFiles,
  DeficiencyVersionFilesCollection,
} from '../deficiencyVersionFiles/queryBuilder';
import { RxdbCollectionName } from '../rxdbCollectionName';
import { Deficiency } from './../../models/models';
import { DeficiencyList, DeficiencyListCollection } from './queryBuilder';

const mapFilesForActiveDeficiency = (
  deficiency: DeficiencyList,
  files: DeficiencyFiles[],
  versionFiles: DeficiencyVersionFiles[],
  inspectionId: number,
) => {
  let deficiencyFiles = versionFiles?.filter(
    def =>
      def.deficiencyId === deficiency.componentFailureId &&
      def.componentVersionInspectionId === inspectionId,
  );

  const fileIds = deficiencyFiles.map(x => x.fileId);
  deficiencyFiles = [
    ...deficiencyFiles,
    ...files?.filter(
      item => !fileIds.includes(item.fileId) && item.deficiencyId === deficiency.componentFailureId,
    ),
  ];
  return deficiencyFiles.filter(file => file.isDelete !== TRUE);
};

const mapDeficiencies = (
  deficiencyList: DeficiencyList[],
  files: DeficiencyFiles[],
  versionFiles: DeficiencyVersionFiles[],
  deficiencyActionedList: DeficiencyActionedList[] = [],
  removalList: DeficiencyComponentRemoval[] = [],
  isActiveDeficiency = false,
  inspectionId = 0,
  inspectionStatus = 0,
) => {
  const isHideDeficiencyFiles = [
    InspectionStatus.SC_PENDING_APPROVAL,
    InspectionStatus.LI_SIGN_PENDING,
    InspectionStatus.FI_SIGN_PENDING,
  ].includes(inspectionStatus);
  return deficiencyList
    .map((deficiency: DeficiencyList) => ({
      ...deficiency,
      deficiencyFiles: !isHideDeficiencyFiles
        ? isActiveDeficiency
          ? mapFilesForActiveDeficiency(deficiency, files, versionFiles, inspectionId)
          : files?.filter(
              (file: DeficiencyFiles) => file?.deficiencyUUID === deficiency.pkDeficiencyUUID,
            )
        : [],
      deficiencyActionedList: deficiencyActionedList?.find(
        (action: DeficiencyActionedList) => action.pkDeficiencyUUID === deficiency.pkDeficiencyUUID,
      ),
      deficiencyComponentRemoval: removalList?.find(
        (removal: DeficiencyComponentRemoval) =>
          removal.componentVersionUUId === deficiency.componentVersionUUID,
      ),
    }))
    .filter(d => d);
};

const filterActiveDeficiencies = (
  activeDeficiencies: [DeficiencyList],
  pkDeficiencyUUIDs: [string],
  inspectionId: number,
) => {
  const removedDeficiencyUuid = activeDeficiencies
    .filter((deficiency: Deficiency) =>
      [ComponentRemovalCategories.ACTIONED, ComponentRemovalCategories.DUPLICATED].includes(
        deficiency?.deficiencyComponentRemoval?.componentRemovalIdBySC,
      ),
    )
    .map(deficiency => deficiency.pkDeficiencyUUID);

  const tempActiveDef = activeDeficiencies.filter(
    (deficiency: Deficiency) =>
      ![ComponentRemovalCategories.ACTIONED, ComponentRemovalCategories.DUPLICATED].includes(
        deficiency?.deficiencyComponentRemoval?.componentRemovalIdBySC,
      ),
  );

  const filtereDeficiencies = pkDeficiencyUUIDs
    .filter(id => !removedDeficiencyUuid.includes(id))
    .map(id => {
      const matchingRecords = tempActiveDef.filter(
        (deficiency: Deficiency) => deficiency.pkDeficiencyUUID === id,
      );
      if (matchingRecords.length === 1) return matchingRecords;
      else {
        const recordMatchingInspectionId = matchingRecords.find(
          (deficiency: Deficiency) => deficiency.componentVersionInspectionId === inspectionId,
        );
        if (recordMatchingInspectionId && Object.keys(recordMatchingInspectionId)?.length > 0) {
          return recordMatchingInspectionId;
        } else {
          return matchingRecords.find(
            (deficiency: Deficiency) => deficiency.componentVersionInspectionId === null,
          );
        }
      }
    })
    .filter(d => d);
  return filtereDeficiencies.flat();
};

const useDeficiencies = (
  inspectionTaskId: number,
  inspectionId: number,
  assetId: number,
  inspectionStatus: number,
) => {
  const { deficiencyActionedList } = useDeficiencyActionedList(assetId);
  const { deficiencyComponentRemoval } = useDeficiencyComponentRemoval(assetId);

  const getDeficiencyFilesQuery = useCallback(
    (collection: DeficiencyFilesCollection) =>
      collection?.find({
        selector: {
          $and: [{ assetInspectionTask: inspectionTaskId }, { isDelete: 'False' }],
        },
      }),
    [inspectionTaskId],
  );

  const getDeficienciesQuery = useCallback(
    (collection: DeficiencyListCollection) =>
      collection?.find({
        selector: {
          $and: [
            { inspectionTaskId },
            { isDelete: 'False' },
            { isReInspectedComponent: { $ne: true } },
          ],
        },
      }),
    [inspectionTaskId],
  );

  const pkDeficiencyUUIDs = useMemo(() => {
    return deficiencyActionedList.map(item => item.pkDeficiencyUUID) || [];
  }, [deficiencyActionedList]);

  const getActiveDeficiencyFilesQuery = useCallback(
    (collection: DeficiencyFilesCollection) =>
      collection?.find({
        selector: {
          $and: [{ assetId }, { isDelete: 'False' }],
        },
      }),
    [assetId],
  );

  const getActiveDeficiencyVersionFilesQuery = useCallback(
    (collection: DeficiencyVersionFilesCollection) =>
      collection?.find({
        selector: {
          $and: [{ assetId }],
        },
      }),
    [assetId],
  );

  const getActiveDeficiencyListQuery = useCallback(
    (collection: DeficiencyListCollection) =>
      collection?.find({
        selector: {
          pkDeficiencyUUID: { $in: pkDeficiencyUUIDs },
        },
      }),
    [pkDeficiencyUUIDs],
  );
  const { result: files } = useRxData<DeficiencyFiles>(
    RxdbCollectionName.DEFICIENCY_FILES,
    getDeficiencyFilesQuery,
    { json: true },
  );

  const { result: activeDeficiencyFiles } = useRxData<DeficiencyFiles>(
    RxdbCollectionName.DEFICIENCY_FILES,
    getActiveDeficiencyFilesQuery,
    { json: true },
  );

  const { result: activeDeficiencyVersionFiles } = useRxData<DeficiencyVersionFiles>(
    RxdbCollectionName.DEFICIENCY_VERSION_FILES,
    getActiveDeficiencyVersionFilesQuery,
    { json: true },
  );
  const { result: deficiencyList } = useRxData<DeficiencyList>(
    RxdbCollectionName.DEFICIENCY_LIST,
    getDeficienciesQuery,
    { json: true },
  );

  const { result: activeDeficiencyList } = useRxData<DeficiencyList>(
    RxdbCollectionName.DEFICIENCY_LIST,
    getActiveDeficiencyListQuery,
    { json: true },
  );

  const deficiencies = useMemo(() => {
    return mapDeficiencies(
      deficiencyList,
      files,
      [],
      [],
      [],
      false,
      inspectionId,
      inspectionStatus,
    );
  }, [deficiencyList, files, inspectionId, inspectionStatus]);

  const tempActiveDeficiencies = useMemo(() => {
    return mapDeficiencies(
      activeDeficiencyList,
      activeDeficiencyFiles,
      activeDeficiencyVersionFiles,
      deficiencyActionedList || [],
      deficiencyComponentRemoval || [],
      true,
      inspectionId,
      inspectionStatus,
    );
  }, [
    activeDeficiencyList,
    activeDeficiencyFiles,
    activeDeficiencyVersionFiles,
    deficiencyActionedList,
    deficiencyComponentRemoval,
    inspectionId,
    inspectionStatus,
  ]);

  const activeDeficiencies = useMemo(() => {
    return filterActiveDeficiencies(tempActiveDeficiencies, pkDeficiencyUUIDs, inspectionId);
  }, [tempActiveDeficiencies, pkDeficiencyUUIDs, inspectionId]);

  return { deficiencies, activeDeficiencies };
};
export default useDeficiencies;
